import { DateTime } from 'luxon'
import { BaseTierDto, isActiveTier, isUserTier } from '../../../../config/finance/financeConfig'
import { UserListItemDto } from '../../../../config/userConfig'
import { parseNumber } from '../../../../helpers/numberHelper'

// Number of weeks shown at once. The last row is next week, so at most one empty row is visible.
export const CALENDAR_WEEK_COUNT = 8

export type DailyTotalCurrencyDto = {
    currency: string
    // Turnover of billable time that already sits on an invoice line.
    turnoverInvoiced: string
    // Turnover of billable time that still has to be invoiced.
    turnoverOutstanding: string
    duration: number
}

export type DailyTotalDto = {
    date: string
    duration: number
    durationBillable: number
    durationOffered: number
    durationNotBillable: number
    currencies: DailyTotalCurrencyDto[]
}

// An amount in a single currency; currencies are never converted, so totals stay split per currency.
export type CurrencyAmount = {
    currency: string
    amount: number
}

export type MonthTotals = {
    // Every currency written in the period, so a total of zero can still be shown in the right currency.
    currencies: string[]
    minutes: number
    minutesBillable: number
    minutesOffered: number
    minutesNotBillable: number
    invoiced: CurrencyAmount[]
    outstanding: CurrencyAmount[]
    expected: CurrencyAmount[]
}

export type CalendarWeek = {
    // Monday of the week, always at the start of the day.
    monday: DateTime
    weekNumber: number
}

export enum DayColor {
    None = 'NONE',
    Red = 'RED',
    Orange = 'ORANGE',
    Green = 'GREEN',
}

/**
 * Builds the visible weeks from old to new, so the newest week is the last row and scrolling up walks into
 * the past. {@code lastMonday} is the newest visible week.
 */
export function buildWeeks(lastMonday: DateTime, count: number = CALENDAR_WEEK_COUNT): CalendarWeek[] {
    return Array.from({ length: count }, (_value, index) => {
        const monday = lastMonday.minus({ weeks: count - 1 - index })
        return { monday, weekNumber: monday.weekNumber }
    })
}

/**
 * Monday of the newest week that may be shown: next week, so the user always sees one week ahead.
 */
export function maxLastMonday(): DateTime {
    return DateTime.local().startOf('week').plus({ weeks: 1 })
}

export function sumDurations(days: DailyTotalDto[]): number {
    return days.reduce((total, day) => total + (day?.duration ?? 0), 0)
}

/**
 * Adds up an amount of the given days per currency, keeping the currencies separate (no conversion).
 */
function sumPerCurrency(days: DailyTotalDto[], amount: (entry: DailyTotalCurrencyDto) => number): CurrencyAmount[] {
    const totals = new Map<string, number>()
    days.forEach(day => (day?.currencies ?? []).forEach(entry => totals.set(entry.currency, (totals.get(entry.currency) ?? 0) + amount(entry))))

    return Array.from(totals.entries())
        .filter(([, total]) => total !== 0)
        .map(([currency, total]) => ({ currency, amount: total }))
}

const invoiced = (entry: DailyTotalCurrencyDto) => parseNumber(entry.turnoverInvoiced, 'en') || 0
const outstanding = (entry: DailyTotalCurrencyDto) => parseNumber(entry.turnoverOutstanding, 'en') || 0

/**
 * The turnover that has been written: what is already invoiced plus what still has to be invoiced.
 */
export function sumTurnoverPerCurrency(days: DailyTotalDto[]): CurrencyAmount[] {
    return sumPerCurrency(days, entry => invoiced(entry) + outstanding(entry))
}

/**
 * Everything the header info label shows for the visible month.
 */
export function sumMonth(days: DailyTotalDto[]): MonthTotals {
    return {
        currencies: Array.from(new Set(days.flatMap(day => (day?.currencies ?? []).map(entry => entry.currency)))),
        minutes: sumDurations(days),
        minutesBillable: days.reduce((total, day) => total + (day?.durationBillable ?? 0), 0),
        minutesOffered: days.reduce((total, day) => total + (day?.durationOffered ?? 0), 0),
        minutesNotBillable: days.reduce((total, day) => total + (day?.durationNotBillable ?? 0), 0),
        invoiced: sumPerCurrency(days, invoiced),
        outstanding: sumPerCurrency(days, outstanding),
        expected: sumTurnoverPerCurrency(days),
    }
}

/**
 * The hour norm in minutes that applies to the given week, or undefined when the user has no norm then.
 *
 * <p>The norm lives on the user tier that is valid on the monday of that week, so a norm that changes per period
 * is handled by looking it up per week.
 */
export function resolveWeekNormInMinutes(user: UserListItemDto | undefined, monday: DateTime): number | undefined {
    const tier = (user?.tiers ?? []).find((candidate: BaseTierDto) => isUserTier(candidate) && isActiveTier(candidate, monday))
    if (!tier || !isUserTier(tier) || !tier.standardContractHoursPerWeek) {
        return undefined
    }

    const hours = parseNumber(tier.standardContractHoursPerWeek, 'en')
    return hours > 0 ? Math.round(hours * 60) : undefined
}

/**
 * Colour of a single day cell. Weekend days and days in the future are never coloured: nothing is expected there
 * (yet). A workday without written time is red, up to the threshold orange and above it green.
 */
export function resolveDayColor(date: DateTime, minutes: number, thresholdMinutes: number, colorsActive: boolean): DayColor {
    if (!colorsActive || date.weekday > 5 || date.startOf('day') > DateTime.local().startOf('day')) {
        return DayColor.None
    }

    if (minutes <= 0) {
        return DayColor.Red
    }

    return minutes <= thresholdMinutes ? DayColor.Orange : DayColor.Green
}

/**
 * Colour of the week total. Only closed weeks are measured against the norm; the current and future weeks have
 * not had the chance to reach it yet.
 */
export function resolveWeekColor(monday: DateTime, minutes: number, normInMinutes: number | undefined, colorsActive: boolean): DayColor {
    if (!colorsActive || !normInMinutes || monday.plus({ weeks: 1 }) > DateTime.local()) {
        return DayColor.None
    }

    return minutes >= normInMinutes ? DayColor.Green : DayColor.Orange
}
